import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { useTranslations } from 'hooks/useTranslations';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { formatDate } from 'utils/date';
import {
  ELicenceAuthoritySftpAlertCategory,
  ILicenceAuthoritySftpAlertStatus,
} from 'models/licenceAuthority';
import { getLicenceAuthoritySftpAlertStatus } from 'requests/licenceAuthorities';

interface ISftpAlertBanner {
  licenceAuthorityUniqueId?: string;
}

const sftpAlertCategoryVariant = (
  category: ELicenceAuthoritySftpAlertCategory,
): EAlertVariants => {
  switch (category) {
    case ELicenceAuthoritySftpAlertCategory.MANIFEST_FAILING:
    case ELicenceAuthoritySftpAlertCategory.SENDING_FAILING:
      return EAlertVariants.error;
    case ELicenceAuthoritySftpAlertCategory.SENDING_BLOCKED_CONFIG:
    case ELicenceAuthoritySftpAlertCategory.SENDING_PAUSED:
      return EAlertVariants.warning;
    default:
      return EAlertVariants.error;
  }
};

export const SftpAlertBanner = ({ licenceAuthorityUniqueId }: ISftpAlertBanner) => {
  const { t } = useTranslations();
  const { longDateFormat } = useGlobalProperty();
  const [alertStatus, setAlertStatus] = useState<ILicenceAuthoritySftpAlertStatus | null>(
    null,
  );

  useEffect(() => {
    (async () => {
      if (!licenceAuthorityUniqueId) {
        setAlertStatus(null);
        return;
      }
      const data = await getLicenceAuthoritySftpAlertStatus(licenceAuthorityUniqueId);
      setAlertStatus(data ?? null);
    })();
  }, [licenceAuthorityUniqueId]);

  if (
    !alertStatus ||
    alertStatus.category === ELicenceAuthoritySftpAlertCategory.OK ||
    !alertStatus.messageKey
  ) {
    return null;
  }

  const sinceText = alertStatus.since
    ? ` ${t('general.since.label')} ${formatDate(alertStatus.since, longDateFormat)}`
    : null;
  const messageKeyText = alertStatus.messageKey ? t(alertStatus.messageKey as string) : null;

  return (
    <Box sx={{ mb: 2 }}>
      {messageKeyText && sinceText && (
        <Alert
          text={`${messageKeyText}${sinceText}`}
          variant={sftpAlertCategoryVariant(alertStatus.category)}
        />
      )}
    </Box>
  );
};
